/**
 * PageTitleContext – sets document.title to the score/result of the user's
 * featured live match (the match they're currently viewing or the most recently viewed).
 * Title rotates (scrolling effect) through: score, player/team names, and winner info.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { usePlayerCache } from './PlayerCacheContext';

const STORAGE_KEY = 'gradedplay_featuredMatchId';
const DEFAULT_TITLE = 'GradedPlay';
const ROTATE_INTERVAL_MS = 2800;

const PageTitleContext = createContext();

export function usePageTitle() {
  return useContext(PageTitleContext);
}

/** Build score string from match.scores (e.g. "21-18, 19-21, 11-9" or "21-18, 19-21"). */
function formatScoreString(scores) {
  if (!scores) return '';
  const parts = [];
  for (const key of ['game1', 'game2', 'game3']) {
    const game = scores[key];
    if (game && typeof game.player1 === 'number' && typeof game.player2 === 'number') {
      parts.push(`${game.player1}-${game.player2}`);
    }
  }
  return parts.join(', ');
}

/**
 * Build rotating title strings: score, names, and (if final) winner.
 * @param {object} matchData - match doc with scores, matchType, team1Name, team2Name, player1Id, player2Id
 * @param {function} getPlayerName - (playerId) => displayName
 * @returns {string[]} array of title strings to cycle through
 */
function buildRotatingTitles(matchData, getPlayerName) {
  if (!matchData) return [];
  const scores = matchData.scores;
  const scoreStr = formatScoreString(scores) || '0-0';
  const isDoubles = matchData.matchType === 'doubles';
  const name1 = isDoubles
    ? (matchData.team1Name || 'Team 1')
    : (getPlayerName(matchData.player1Id) || 'Player 1');
  const name2 = isDoubles
    ? (matchData.team2Name || 'Team 2')
    : (getPlayerName(matchData.player2Id) || 'Player 2');
  const vs = `${name1} vs ${name2}`;
  const isFinal = !!(scores?.matchWinner);
  const winnerName = scores?.matchWinner === 'player1' ? name1 : name2;

  const titles = [];
  if (isFinal) {
    titles.push(`Final: ${scoreStr} | ${vs}`);
    titles.push(`Winner: ${winnerName} | ${scoreStr}`);
    titles.push(`${vs} | ${scoreStr} • Winner: ${winnerName}`);
  } else {
    titles.push(`Live: ${scoreStr} | ${vs}`);
    titles.push(`${vs} | ${scoreStr}`);
    titles.push(`Live: ${vs} | ${scoreStr}`);
  }
  return titles;
}

export function PageTitleProvider({ children }) {
  const location = useLocation();
  const pathname = location?.pathname || '';
  const { getPlayers, getPlayerName } = usePlayerCache();

  // Featured match: from URL when on /match/:matchId, else from localStorage (most recently viewed)
  const [featuredMatchId, setFeaturedMatchIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch (_) {
      return null;
    }
  });

  // Match data from Firestore (for building titles with names)
  const [matchData, setMatchData] = useState(null);
  const rotateIndexRef = useRef(0);

  // Sync featured match from route: when user is on match page, that becomes the featured match
  useEffect(() => {
    const match = pathname.match(/^\/match\/([^/]+)$/);
    const id = match ? match[1] : null;
    if (id) {
      setFeaturedMatchIdState(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch (_) {}
    } else {
      const stored = (() => {
        try {
          return localStorage.getItem(STORAGE_KEY) || null;
        } catch (_) {
          return null;
        }
      })();
      if (stored && !pathname.startsWith('/match/')) {
        setFeaturedMatchIdState(stored);
      }
    }
  }, [pathname]);

  // Subscribe to featured match; resolve player names for singles
  useEffect(() => {
    if (!featuredMatchId) {
      setMatchData(null);
      document.title = DEFAULT_TITLE;
      return undefined;
    }

    const matchRef = doc(db, 'matches', featuredMatchId);
    const unsubscribe = onSnapshot(
      matchRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setMatchData(null);
          document.title = DEFAULT_TITLE;
          return;
        }
        const data = { id: snapshot.id, ...snapshot.data() };
        setMatchData(data);
        // Prime player cache for singles so getPlayerName works when we build titles
        if (data.matchType !== 'doubles' && (data.player1Id || data.player2Id)) {
          getPlayers([data.player1Id, data.player2Id].filter(Boolean)).catch(() => {});
        }
      },
      (err) => {
        console.warn('[PageTitleContext] snapshot error', err);
        setMatchData(null);
        document.title = DEFAULT_TITLE;
      }
    );

    return () => {
      unsubscribe();
      setMatchData(null);
      document.title = DEFAULT_TITLE;
    };
  }, [featuredMatchId, getPlayers]);

  // Build rotating title strings (depends on matchData + getPlayerName / player cache)
  const rotatingTitles = useMemo(
    () => (matchData ? buildRotatingTitles(matchData, getPlayerName) : []),
    [matchData, getPlayerName]
  );

  // Scroll/rotate document.title through the title strings
  useEffect(() => {
    if (rotatingTitles.length === 0) {
      document.title = DEFAULT_TITLE;
      return undefined;
    }
    const tick = () => {
      rotateIndexRef.current = (rotateIndexRef.current + 1) % rotatingTitles.length;
      document.title = rotatingTitles[rotateIndexRef.current];
    };
    document.title = rotatingTitles[0];
    rotateIndexRef.current = 0;
    const intervalId = setInterval(tick, ROTATE_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
      document.title = DEFAULT_TITLE;
    };
  }, [rotatingTitles]);

  const value = {
    featuredMatchId,
    matchTitle: rotatingTitles[0] ?? null,
    rotatingTitles,
    setFeaturedMatchId: (id) => {
      setFeaturedMatchIdState(id || null);
      try {
        if (id) localStorage.setItem(STORAGE_KEY, id);
        else localStorage.removeItem(STORAGE_KEY);
      } catch (_) {}
    },
  };

  return (
    <PageTitleContext.Provider value={value}>
      {children}
    </PageTitleContext.Provider>
  );
}
